import type { MouseEvent } from "react";
import EmptyStarIcon from "@/assets/svgs/star-empty.svg";
import HalfStarIcon from "@/assets/svgs/star-half.svg";
import StarIcon from "@/assets/svgs/star.svg";
import { Tooltip } from "@/components/shared/data-display/tooltip/tooltip";
import { Link } from "@/components/shared/inputs/link/link";
import styles from "./work-rating.module.scss";

const RATING_LABELS = [
  "I hate it.",
  "I didn't like it.",
  "It was OK.",
  "I liked it.",
  "I loved it!",
] as const;

type StarFill = "empty" | "half" | "full";

const getStarFill = (value: number, index: number): StarFill => {
  const normalizedValue = Math.min(5, Math.max(0, value));
  // Match Legacy's React getRating utility. It displays only full, half, and
  // empty stars; the older server-rendered C# helper follows different rules.
  const wholeStars = Math.floor(Math.round(normalizedValue * 2) / 2);
  const fraction = normalizedValue % 1;

  if (index < wholeStars) return "full";
  if (index === wholeStars && fraction > 0.25 && fraction < 0.75) {
    return "half";
  }
  return "empty";
};

const STAR_ICONS = {
  empty: EmptyStarIcon,
  half: HalfStarIcon,
  full: StarIcon,
} as const;

interface WorkRatingProps {
  value: number;
  count: number;
  showReviewsLink?: boolean;
}

const WorkRating = ({
  value,
  count,
  showReviewsLink = true,
}: WorkRatingProps) => {
  const handleReviewsClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById("customer-reviews")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className={styles.root}>
      <span
        className={styles.stars}
        role="img"
        aria-label={`${value.toFixed(1)} out of 5 stars from ${count} ratings`}
      >
        {RATING_LABELS.map((label, index) => {
          const rating = index + 1;
          const fill = getStarFill(value, index);
          const RatingIcon = STAR_ICONS[fill];
          return (
            <Tooltip
              key={rating}
              content={label}
              position="top"
              enterDelay={400}
              arrow
            >
              <span className={styles.star} data-star-fill={fill} aria-hidden>
                <RatingIcon className={styles.starIcon} />
              </span>
            </Tooltip>
          );
        })}
      </span>
      {showReviewsLink && (
        <Link
          href="#customer-reviews"
          linkType="external"
          onClick={handleReviewsClick}
          className={styles.reviewsLink}
        >
          See Reviews
        </Link>
      )}
    </div>
  );
};

export default WorkRating;
